import { Button, PlusIcon, styled } from 'canoe';
import { useFormikContext } from 'formik';
import React, { useCallback, useMemo } from 'react';

import { hasEditor, useHasPermission } from 'commons/auths';
import { DefinitionObjectSelector } from 'dashboard/pages/Segment/components/DefinitionFormFields';
import { DefinitionFormikValues } from 'dashboard/pages/Segment/components/types';

import { RuleContent, RuleIndex, Rules, RuleSection, Wrapper } from './Rules';
import { addRuleToRuleGroup, removeRuleFromRuleGroup } from './utils';

const AddButton = styled.div`
  width: 165px;
`;

export interface RuleGroupProps {
  readOnly?: boolean;
}

export const RuleGroup = React.memo(({ readOnly }: RuleGroupProps) => {
  const { values, setValues } = useFormikContext<DefinitionFormikValues>();

  const definition = useMemo(() => values.definition, [values]);

  const onAddRule = useCallback(() => {
    setValues({ definition: addRuleToRuleGroup(definition) });
  }, [setValues, addRuleToRuleGroup, definition]);

  const onRemoveRule = useCallback(
    (index: number) => {
      setValues({ definition: removeRuleFromRuleGroup(definition, index) });
    },
    [setValues, removeRuleFromRuleGroup, definition],
  );

  const canEdit = useHasPermission(hasEditor);

  return (
    <div data-cy="segment-definition-rule-group">
      <Rules definition={definition} readOnly={readOnly} onRemoveRule={onRemoveRule} />
      {!readOnly && (
        <Wrapper>
          <DefinitionObjectSelector
            name="definition.object"
            readOnly={!definition?.groups?.length || !canEdit || readOnly}
            isSingleRule={!definition?.groups?.length}
          />

          <RuleSection>
            <RuleIndex size="long">{(definition?.groups?.length || 1) + 1}</RuleIndex>
            <RuleContent>
              <AddButton>
                <Button
                  variant="secondary"
                  onClick={onAddRule}
                  appearance="outline"
                  disabled={!canEdit}
                  icon={PlusIcon}
                  fullWidth
                  data-cy="segment-add-rule-button"
                >
                  Add Condition
                </Button>
              </AddButton>
            </RuleContent>
          </RuleSection>
        </Wrapper>
      )}
    </div>
  );
});
